import { poiIcons } from "@/constants/mapStyles";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectPOIById } from "@features/survey/poisSelectors";
import { updateCategoryField } from "@features/survey/poisSlice";
import { selectCurrentSurveyId } from "@features/survey/surveySelectors";
import { useCurrentPoiId } from "@hooks/useSurveySelectors";
import { surveyPOIApi, useUpdatePOICategoryMutation } from "@services/survey-api";
import { useState } from "react";

const defaultGray = "#919191";

const PoiIconSelector = () => {
  const surveyId = useAppSelector(selectCurrentSurveyId);
  const dispatch = useAppDispatch();
  const currentPoiId = useCurrentPoiId();
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );
  const categoryId = currentPOI?.categoryId || "";
  const [updatePOICategory] = useUpdatePOICategoryMutation();

  // Read the active color directly from Redux so PoiColorPicker and
  // PoiIconSelector stay in sync without prop drilling.
  const colorUpdate = currentPOI?.category?.iconData?.color || "#a23f97";

  const [iconName, setIconName] = useState(
    currentPOI?.category?.iconData?.iconName || "circle",
  );

  const handleIconSelect = (icon: string) => {
    setIconName(icon);
    
    // 1. Optimistic Redux update
    dispatch(
      updateCategoryField({
        categoryId,
        updates: { iconData: { iconName: icon } },
      }),
    );
    
    // 2. Optimistic RTK Query cache update
    dispatch(
      surveyPOIApi.util.updateQueryData('getPOICategories', surveyId || "", (draft) => {
        const category = draft.find(c => c.extId === categoryId);
        if (category) {
          category.iconData = category.iconData ?? {};
          category.iconData.iconName = icon;
        }
      })
    );
    
    // 3. Persist to backend
    updatePOICategory({
      surveyId: surveyId || "",
      categoryId,
      body: { iconData: { iconName: icon } },
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-base">Select an Icon</p>
      <div className="flex gap-2 flex-wrap iconsWrapper w-[75%]">
        {poiIcons.map((icon) =>
          icon === "circle" ? (
            <svg
              key={icon}
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="cursor-pointer hover:opacity-75 shrink-0"
              onClick={() => handleIconSelect(icon)}
            >
              <circle
                cx="12"
                cy="12"
                r="12"
                fill={icon === iconName ? colorUpdate : defaultGray}
              />
            </svg>
          ) : (
            <svg
              key={icon}
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="cursor-pointer hover:opacity-75 shrink-0"
              onClick={() => handleIconSelect(icon)}
            >
              <circle
                cx="12"
                cy="12"
                r="12"
                fill={icon === iconName ? colorUpdate : defaultGray}
              />
              <text
                x="12"
                y="20"
                textAnchor="middle"
                fontFamily="Material Icons"
                fontSize="16"
                fill="white"
              >
                {icon}
              </text>
            </svg>
          ),
        )}
      </div>
    </div>
  );
};

export default PoiIconSelector;
